import {
	IconArrowRight,
	IconBolt,
	IconChartLine,
	IconClock,
	IconPlugConnected,
	IconTrendingUp,
} from "@tabler/icons-react";
import {
	createFileRoute,
	Link,
	Outlet,
	useLocation,
} from "@tanstack/react-router";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import { Badge } from "@/components/ui/badge";
import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { config } from "@/data/config";
import { cn } from "@/lib/utils";
import { schema, toScript } from "@/utils/schema";
import { seo } from "@/utils/seo";

const LIVE_DASHBOARD_COUNT = 1;

const solarHighlights = [
	{
		label: "Generation",
		description: "Daily production and lifetime totals.",
	},
	{
		label: "Grid usage",
		description: "Import, export, and dependency trends.",
	},
	{
		label: "Payback",
		description: "System cost balance and breakeven estimate.",
	},
] as const;

export const Route = createFileRoute("/_pages/data")({
	component: RouteComponent,
	head: () => {
		const description = "Dashboards and metrics published as they come online.";
		const base = seo({
			title: `Data | ${config.Name}`,
			description,
			keywords: "data dashboard, metrics, analytics, solar, personal data",
			url: `${config.Url}/data`,
		});
		return {
			...base,
			scripts: [
				...base.scripts,
				toScript(
					schema.collectionPage(
						`${config.Name} - Data`,
						`${config.Url}/data`,
						description,
					),
				),
				toScript(
					schema.breadcrumb([
						{ name: config.Name, url: config.Url },
						{ name: "Data", url: `${config.Url}/data` },
					]),
				),
			],
		};
	},
});

function RouteComponent() {
	const { pathname } = useLocation();
	const isDataIndex = pathname === "/data";

	if (!isDataIndex) {
		return <Outlet />;
	}

	return (
		<div className="relative bg-background">
			<div
				className="surface-grid pointer-events-none absolute inset-0 opacity-50"
				aria-hidden
			/>
			<main className="relative pt-12 sm:pt-20">
				<div className="mx-auto max-w-6xl px-4 lg:px-8">
					<div className="animate-fade-in-up">
						<MarketingHeader
							heading="Data"
							description="Dashboards and metrics I publish as they come online."
						/>
					</div>

					<div className="pb-20">
						<section
							className="mb-12 grid animate-in grid-cols-1 gap-px overflow-hidden rounded-xl border border-border/70 bg-border/70 duration-800 fill-mode-both fade-in slide-in-from-bottom-8 sm:grid-cols-3"
							style={{ animationDelay: "0.12s" }}
							aria-label="Data overview"
						>
							<div className="bg-card px-6 py-5">
								<p className="truncate text-sm text-muted-foreground">
									Live dashboards
								</p>
								<p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums">
									{LIVE_DASHBOARD_COUNT}
								</p>
							</div>
							<div className="bg-card px-6 py-5">
								<p className="truncate text-sm text-muted-foreground">
									Update cadence
								</p>
								<p className="mt-2 text-2xl font-semibold tracking-tight">
									Nightly
								</p>
							</div>
							<div className="bg-card px-6 py-5">
								<p className="truncate text-sm text-muted-foreground">
									Primary source
								</p>
								<p className="mt-2 text-2xl font-semibold tracking-tight">
									Enphase
								</p>
							</div>
						</section>

						<section className="mb-16">
							<div
								className="mb-8 flex animate-in items-center gap-3 duration-800 fill-mode-both fade-in slide-in-from-bottom-8"
								style={{ animationDelay: "0.24s" }}
							>
								<div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
									<IconChartLine className="size-5" />
								</div>
								<div className="min-w-0">
									<h2 className="text-3xl font-semibold tracking-tight text-foreground">
										Live dashboards
									</h2>
									<p className="mt-1 text-sm text-muted-foreground">
										<span className="tabular-nums">{LIVE_DASHBOARD_COUNT}</span>{" "}
										{LIVE_DASHBOARD_COUNT === 1 ? "dashboard" : "dashboards"}{" "}
										available now.
									</p>
								</div>
							</div>

							<Link
								to="/data/solar"
								className="block animate-in duration-800 fill-mode-both fade-in slide-in-from-bottom-8"
								style={{ animationDelay: "0.36s" }}
							>
								<Card className="group relative overflow-hidden border-border/80 bg-card shadow-lg transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-xl dark:shadow-none dark:hover:shadow-none">
									<div className="pointer-events-none absolute inset-0 bg-linear-to-br from-primary/6 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-primary/4" />
									<CardHeader className="relative gap-0 pb-0">
										<div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
											<div className="min-w-0 flex-1">
												<div className="mb-4 flex flex-wrap items-center gap-2">
													<Badge variant="success">Live</Badge>
													<Badge variant="outline">Enphase</Badge>
													<Badge
														variant="secondary"
														className="gap-1 py-1 pr-2 pl-1"
													>
														<IconClock className="size-4 shrink-0" />
														Updated nightly
													</Badge>
												</div>
												<CardTitle className="flex items-center gap-2 text-2xl font-semibold text-foreground transition-colors group-hover:text-primary sm:text-3xl">
													<IconBolt className="size-5 shrink-0 text-primary" />
													Home solar
												</CardTitle>
												<CardDescription className="mt-3 max-w-[56ch] text-base text-pretty text-muted-foreground">
													Generation, grid import and export, consumption
													trends, and payback progress from my rooftop system.
												</CardDescription>
											</div>

											<dl className="grid min-w-0 shrink-0 grid-cols-1 gap-4 rounded-xl border border-border/70 bg-muted/30 p-4 sm:grid-cols-3 lg:w-[min(100%,28rem)] lg:grid-cols-1">
												{solarHighlights.map((item) => (
													<div key={item.label} className="min-w-0">
														<dt className="text-sm font-medium text-foreground">
															{item.label}
														</dt>
														<dd className="mt-1 text-sm text-pretty text-muted-foreground">
															{item.description}
														</dd>
													</div>
												))}
											</dl>
										</div>
									</CardHeader>

									<CardFooter className="relative mt-8 flex flex-col gap-4 border-t border-border/70 pt-6 sm:flex-row sm:items-center sm:justify-between">
										<div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
											<span className="inline-flex items-center gap-1.5">
												<IconTrendingUp className="size-4 shrink-0 text-primary" />
												Energy and cost views
											</span>
											<span className="inline-flex items-center gap-1.5">
												<IconPlugConnected className="size-4 shrink-0 text-primary" />
												Historical daily records
											</span>
										</div>
										<div className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-md transition-colors group-hover:bg-primary/90 sm:w-auto dark:shadow-none">
											<span>View dashboard</span>
											<IconArrowRight className="size-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
										</div>
									</CardFooter>
								</Card>
							</Link>
						</section>

						<section
							className={cn(
								"animate-in duration-800 fill-mode-both fade-in slide-in-from-bottom-8",
								"rounded-xl border border-dashed border-border/70 bg-muted/20 px-6 py-8 sm:px-8",
							)}
							style={{ animationDelay: "0.48s" }}
						>
							<h2 className="text-xl font-semibold tracking-tight text-foreground">
								More dashboards coming
							</h2>
							<p className="mt-2 max-w-[56ch] text-base text-pretty text-muted-foreground">
								Additional data views will land here as I wire up new sources
								and experiments.
							</p>
						</section>
					</div>
				</div>
			</main>
		</div>
	);
}
